/* OPS Pattern Engine v0.1.0
 * Product-independent 2D pattern placement and Canvas rendering.
 * Source artwork stays separate from pattern settings and product surfaces.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) {
    root.OPS = root.OPS || {};
    root.OPS.PatternEngine = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const VERSION = '0.1.0';
  const REPEAT_MODES = new Set(['repeat', 'horizontal', 'vertical', 'stagger', 'half-drop', 'brick', 'random']);
  const SCALE_MODES = new Set(['single', 'fit', 'cover', 'stretch']);
  const MIRROR_MODES = new Set(['mirror-2', 'mirror-4']);
  const ALL_MODES = new Set([...REPEAT_MODES, ...SCALE_MODES, ...MIRROR_MODES, 'mirror-8']);

  function positive(value, name) {
    const number = Number(value);
    if (!Number.isFinite(number) || number <= 0) throw new RangeError(`${name} must be greater than zero`);
    return number;
  }

  function validateSurface(profile) {
    if (!profile || typeof profile !== 'object') throw new TypeError('Surface profile is required');
    const widthMm = positive(profile.widthMm, 'widthMm');
    const heightMm = positive(profile.heightMm, 'heightMm');
    const bleedMm = Math.max(0, Number(profile.bleedMm) || 0);
    const safeArea = profile.safeArea || { topMm: 0, rightMm: 0, bottomMm: 0, leftMm: 0 };
    for (const side of ['topMm', 'rightMm', 'bottomMm', 'leftMm']) {
      if (!Number.isFinite(Number(safeArea[side])) || Number(safeArea[side]) < 0) throw new RangeError(`safeArea.${side} must be zero or greater`);
    }
    if (Number(safeArea.leftMm) + Number(safeArea.rightMm) >= widthMm || Number(safeArea.topMm) + Number(safeArea.bottomMm) >= heightMm) {
      throw new RangeError('Safe area must leave a printable rectangle');
    }
    return {
      ...profile,
      widthMm,
      heightMm,
      bleedMm,
      safeArea: Object.fromEntries(['topMm', 'rightMm', 'bottomMm', 'leftMm'].map(k => [k, Number(safeArea[k]) || 0]))
    };
  }

  function mulberry32(seed) {
    let state = Number(seed) >>> 0;
    return function () {
      state += 0x6D2B79F5;
      let t = state;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function normalizePattern(input = {}) {
    const mode = input.mode || 'repeat';
    if (!ALL_MODES.has(mode)) throw new RangeError(`Unsupported pattern mode: ${mode}`);
    return {
      ...input,
      mode,
      tileWidth: positive(input.tileWidth || input.cellPx || 200, 'tileWidth'),
      tileHeight: positive(input.tileHeight || input.cellPx || 200, 'tileHeight'),
      gapX: Math.max(0, Number(input.gapX ?? input.gapPx ?? 0) || 0),
      gapY: Math.max(0, Number(input.gapY ?? input.gapPx ?? 0) || 0),
      offsetX: Number(input.offsetX ?? input.offsetXPx ?? 0) || 0,
      offsetY: Number(input.offsetY ?? input.offsetYPx ?? 0) || 0,
      seed: Number(input.seed ?? 1) >>> 0,
      randomRotation: Boolean(input.randomRotation),
      randomScale: Math.max(0, Math.min(0.9, Number(input.randomScale) || 0))
    };
  }

  function transformFor(mode, row, column) {
    if (mode === 'mirror-2') return { scaleX: column % 2 ? -1 : 1, scaleY: 1, rotation: 0 };
    if (mode === 'mirror-4') return { scaleX: column % 2 ? -1 : 1, scaleY: row % 2 ? -1 : 1, rotation: 0 };
    return { scaleX: 1, scaleY: 1, rotation: 0 };
  }

  function wraparoundCopies(bounds, tileWidth, tileHeight) {
    const width = positive(tileWidth, 'tile width');
    const height = positive(tileHeight, 'tile height');
    const x = Number(bounds?.x);
    const y = Number(bounds?.y);
    const itemWidth = positive(bounds?.width, 'artwork width');
    const itemHeight = positive(bounds?.height, 'artwork height');
    if (!Number.isFinite(x) || !Number.isFinite(y)) throw new TypeError('Artwork x and y must be finite numbers');
    const minCol = Math.floor((-x - itemWidth) / width) + 1;
    const maxCol = Math.ceil((width - x) / width) - 1;
    const minRow = Math.floor((-y - itemHeight) / height) + 1;
    const maxRow = Math.ceil((height - y) / height) - 1;
    const copies = [];
    for (let row = minRow; row <= maxRow; row++) for (let column = minCol; column <= maxCol; column++) {
      copies.push({ ...bounds, x: x + column * width, y: y + row * height, wrapColumn: column, wrapRow: row });
    }
    return copies;
  }

  function placements(width, height, input = {}) {
    positive(width, 'surface width');
    positive(height, 'surface height');
    const p = normalizePattern(input);
    if (SCALE_MODES.has(p.mode) || p.mode === 'mirror-8') return [];
    const groupCols = p.mode === 'mirror-2' || p.mode === 'mirror-4' ? 2 : 1;
    const groupRows = p.mode === 'mirror-4' ? 2 : 1;
    const groupW = groupCols * p.tileWidth + (groupCols - 1) * p.gapX;
    const groupH = groupRows * p.tileHeight + (groupRows - 1) * p.gapY;
    const stepX = groupW + p.gapX;
    const stepY = groupH + p.gapY;
    const maxCells = Math.max(1, Number(p.maxCells) || 10000);
    const result = [];
    const rand = mulberry32(p.seed);
    const rowStart = p.mode === 'vertical' ? 0 : Math.floor((-p.offsetY - groupH) / stepY);
    const rowEnd = p.mode === 'horizontal' ? 0 : Math.ceil((height - p.offsetY + groupH) / stepY);
    const colStart = p.mode === 'horizontal' ? 0 : Math.floor((-p.offsetX - groupW) / stepX);
    const colEnd = p.mode === 'vertical' ? 0 : Math.ceil((width - p.offsetX + groupW) / stepX);
    for (let row = rowStart; row <= rowEnd; row++) {
      for (let column = colStart; column <= colEnd; column++) {
        let x = p.offsetX + column * stepX;
        let y = p.offsetY + row * stepY;
        if (p.mode === 'horizontal' || p.mode === 'vertical') {
          // A one-axis repeat centers the un-repeated tile on the other axis.
          if (p.mode === 'horizontal') y = (height - p.tileHeight) / 2 + p.offsetY;
          if (p.mode === 'vertical') x = (width - p.tileWidth) / 2 + p.offsetX;
        }
        if (p.mode === 'stagger' && Math.abs(row % 2) === 1) x += stepX / 2;
        if (p.mode === 'half-drop' && Math.abs(column % 2) === 1) y += stepY / 2;
        if (p.mode === 'brick' && Math.abs(row % 2) === 1) x += stepX / 2;
        if (p.mode === 'random') {
          x += (rand() - 0.5) * p.tileWidth * 0.3;
          y += (rand() - 0.5) * p.tileHeight * 0.3;
        }
        if (x + groupW < 0 || y + groupH < 0 || x > width || y > height) continue;
        for (let gr = 0; gr < groupRows; gr++) for (let gc = 0; gc < groupCols; gc++) {
          if (result.length >= maxCells) throw new RangeError(`Pattern exceeds maxCells (${maxCells})`);
          const transform = transformFor(p.mode, gr, gc);
          const variation = p.mode === 'random' ? {
            rotation: p.randomRotation ? Math.floor(rand() * 4) * 90 : 0,
            scale: 1 + (rand() * 2 - 1) * p.randomScale
          } : { rotation: 0, scale: 1 };
          result.push({
            x: x + gc * (p.tileWidth + p.gapX),
            y: y + gr * (p.tileHeight + p.gapY),
            width: p.tileWidth,
            height: p.tileHeight,
            row,
            column,
            ...transform,
            ...variation
          });
        }
      }
    }
    return result;
  }

  function drawImagePlacement(ctx, source, item) {
    ctx.save();
    ctx.translate(item.x + item.width / 2, item.y + item.height / 2);
    ctx.rotate((item.rotation || 0) * Math.PI / 180);
    ctx.scale((item.scaleX || 1) * (item.scale || 1), (item.scaleY || 1) * (item.scale || 1));
    ctx.drawImage(source, -item.width / 2, -item.height / 2, item.width, item.height);
    ctx.restore();
  }

  function drawEightWay(ctx, source, width, height) {
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.hypot(width, height) * 1.5;
    for (let sector = 0; sector < 8; sector++) {
      const start = sector * Math.PI / 4 - Math.PI / 8;
      const end = start + Math.PI / 4;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, start, end);
      ctx.closePath();
      ctx.clip();
      ctx.translate(cx, cy);
      ctx.rotate(sector * Math.PI / 4);
      if (sector % 2) ctx.scale(-1, 1);
      ctx.drawImage(source, -width / 2, -height / 2, width, height);
      ctx.restore();
    }
  }

  function drawScaleMode(ctx, source, width, height, mode, opts) {
    const sw = positive(source.width || source.naturalWidth, 'source width');
    const sh = positive(source.height || source.naturalHeight, 'source height');
    let dw = width, dh = height, dx = 0, dy = 0;
    if (mode === 'fit' || mode === 'single') {
      const scale = Math.min(width / sw, height / sh, mode === 'single' ? 1 : Infinity);
      dw = sw * scale; dh = sh * scale;
      dx = (width - dw) / 2; dy = (height - dh) / 2;
    } else if (mode === 'cover') {
      const scale = Math.max(width / sw, height / sh);
      dw = sw * scale; dh = sh * scale;
      dx = (width - dw) / 2; dy = (height - dh) / 2;
    }
    if (opts.background) { ctx.fillStyle = opts.background; ctx.fillRect(0, 0, width, height); }
    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, width, height); ctx.clip();
    ctx.drawImage(source, dx, dy, dw, dh);
    ctx.restore();
  }

  function render(ctx, source, width, height, input = {}) {
    if (!ctx || !source) throw new TypeError('Canvas context and source artwork are required');
    const p = normalizePattern(input);
    if (p.background) { ctx.fillStyle = p.background; ctx.fillRect(0, 0, width, height); }
    if (SCALE_MODES.has(p.mode)) {
      drawScaleMode(ctx, source, width, height, p.mode, p);
      return { mode: p.mode, placements: 1 };
    }
    if (p.mode === 'mirror-8') {
      drawEightWay(ctx, source, width, height);
      return { mode: p.mode, placements: 8 };
    }
    const items = placements(width, height, p);
    for (const item of items) drawImagePlacement(ctx, source, item);
    return { mode: p.mode, placements: items.length };
  }

  return Object.freeze({ VERSION, ALL_MODES: Object.freeze([...ALL_MODES]), normalizePattern, validateSurface, placements, wraparoundCopies, render });
});
